import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppError } from '@rms/sdk/errors';
import { isUserRole, User, UserStoreService } from '@rms/user';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';

import { PartnerDashboardAccessDeniedError } from '../errors';
import { AccessTokenClaims, LoginResponse } from '../models';
import { AuthStoreService } from '../store';
import { decodeAccessToken, isAccessTokenExpired } from '../utils';
import { AuthApiService } from './auth-api.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly api = inject(AuthApiService);
  private readonly store = inject(AuthStoreService);
  private readonly userStore = inject(UserStoreService);

  public login(username: string, password: string): Observable<void> {
    return this.api.login(username, password).pipe(
      catchError((error: HttpErrorResponse) =>
        throwError(() =>
          AppError.from(
            error,
            error.status === HttpStatusCode.Unauthorized ? 'auth.errors.invalid-credentials' : 'auth.errors.login'
          )
        )
      ),
      switchMap((response: LoginResponse) => {
        if (!this.hasAccess(decodeAccessToken(response.access_token))) {
          return throwError(() => new PartnerDashboardAccessDeniedError());
        }

        this.store.setToken(response.access_token);

        return of(undefined);
      })
    );
  }

  public logout(): Observable<void> {
    return this.api.logout().pipe(
      catchError(() => of(undefined)),
      tap(() => this.clearSession())
    );
  }

  public clearSession(): void {
    this.store.resetStore();
    this.userStore.resetUser();
  }

  public isAuthenticated(): boolean {
    const claims = this.store.getClaims();

    return !!claims && !isAccessTokenExpired(claims) && this.hasAccess(claims);
  }

  public getCurrentUser(): User | null {
    const claims = this.store.getClaims();

    if (!claims) {
      return null;
    }

    return {
      id: claims.user_id,
      email: claims.username,
      full_name: claims.user_full_name,
      roles: claims.roles.filter(isUserRole),
    };
  }

  private hasAccess(claims: AccessTokenClaims | null): boolean {
    return !!claims && claims.roles.some(isUserRole);
  }
}
